import React from "react";

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  fontSize: "14px",
  fontFamily: "Inter, sans-serif",
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  outline: "none",
  boxSizing: "border-box",
};

/**
 * Editor for a "range" service's price options.
 *
 * Each option is a label + price (plus an optional per-option duration).
 * Customers pick exactly one option at booking time, which becomes the definite
 * charge - so the options must be concrete numbers, never a min/max band.
 *
 * Shared by every service editor (business template, freelancer add/edit,
 * R3 professional add/edit, and the admin platform templates) so the shape
 * saved to the API is identical everywhere.
 */
export const PriceTiersEditor = ({
  tiers = [],
  onChange,
  maxTiers = 6,
  showDuration = true,
}) => {
  const list = Array.isArray(tiers) ? tiers : [];

  const update = (index, patch) => {
    const next = list.map((tier, i) => (i === index ? { ...tier, ...patch } : tier));
    onChange(next);
  };

  const remove = (index) => {
    onChange(list.filter((_, i) => i !== index));
  };

  const add = () => {
    onChange([...list, { label: "", price: "", durationMinutes: "" }]);
  };

  return (
    <div style={{ display: "grid", gap: "10px" }}>
      {list.length > 0 ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: showDuration
              ? "2fr 1fr 1fr auto"
              : "2fr 1fr auto",
            gap: "8px",
            fontSize: "12px",
            fontWeight: "600",
            color: "#475569",
            fontFamily: "Inter, sans-serif",
          }}
        >
          <span>Option label</span>
          <span>Price ($) *</span>
          {showDuration ? <span>Minutes *</span> : null}
          <span />
        </div>
      ) : null}
      {list.map((tier, index) => (
        <div
          key={`price-tier-${index}`}
          style={{
            display: "grid",
            gridTemplateColumns: showDuration
              ? "2fr 1fr 1fr auto"
              : "2fr 1fr auto",
            gap: "8px",
            alignItems: "center",
          }}
        >
          <input
            type="text"
            value={tier.label ?? ""}
            onChange={(e) => update(index, { label: e.target.value })}
            placeholder="Label, e.g. Mid-back"
            style={inputStyle}
          />
          <input
            type="number"
            value={tier.price ?? ""}
            onChange={(e) => update(index, { price: e.target.value })}
            placeholder="Price"
            min="0"
            step="0.01"
            style={inputStyle}
          />
          {showDuration ? (
            <input
              type="number"
              value={tier.durationMinutes ?? ""}
              onChange={(e) => update(index, { durationMinutes: e.target.value })}
              placeholder="Minutes"
              min="1"
              style={inputStyle}
            />
          ) : null}
          <button
            type="button"
            onClick={() => remove(index)}
            style={{
              padding: "10px 12px",
              fontSize: "13px",
              fontFamily: "Inter, sans-serif",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              background: "#fff",
              color: "#b91c1c",
              cursor: "pointer",
            }}
          >
            Remove
          </button>
        </div>
      ))}

      {list.length < maxTiers ? (
        <button
          type="button"
          onClick={add}
          style={{
            justifySelf: "start",
            padding: "10px 14px",
            fontSize: "13px",
            fontFamily: "Inter, sans-serif",
            fontWeight: "600",
            border: "1px solid #bae6fd",
            borderRadius: "8px",
            background: "#fff",
            color: "#0369a1",
            cursor: "pointer",
          }}
        >
          + Add option
        </button>
      ) : null}

      <div
        style={{
          fontSize: "12px",
          color: "#0369a1",
          fontFamily: "Inter, sans-serif",
        }}
      >
        Add 2-{maxTiers} options with a price each. Customers choose one, and that
        becomes the booking price.
        {showDuration
          ? " Minutes is required per option - it sets the appointment length for that option."
          : ""}
      </div>
    </div>
  );
};

export default PriceTiersEditor;
